import React, { useState, useEffect, useMemo } from 'react';
import {
  ArrowRight, Package, Plus, Edit2, Trash2, Send, Check, X,
  Loader, Filter, RotateCcw, Warehouse, Store, CheckCircle,
  AlertCircle, Clock, TrendingUp, FileText, Search
} from 'lucide-react';
import {
  getTransferts, getProduits, getEntrepots, getBoutiques,
  createTransfert, updateTransfert, deleteTransfert,
  validerTransfert, expedierTransfert, recevoirTransfert, annulerTransfert,
  getBonTransfertPDF,
} from '../services/api';
import { useConfig } from '../context/ConfigContext';
import '../styles/Transferts.css';

const fdate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const fmt   = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0)) + ' XOF';

// Choisit la quantité pertinente selon le statut du transfert
const ligneQte = (l, statut) => {
  if (statut === 'expediee') return Number(l.quantiteExpediee || 0);
  if (statut === 'recue')    return Number(l.quantiteRecue    || l.quantiteExpediee || 0);
  return Number(l.quantiteDemandee || 0);
};
// Prix unitaire (achat) du produit pour la valorisation du transfert
const ligneUnitPrix = (l) => {
  const p = l.produit || {};
  return Number(p.prixAchatDetail || p.prixAchat || p.prixAchatGros || 0) || 0;
};
const transfertMontant = (t) =>
  (t.lignes || []).reduce((s, l) => s + ligneQte(l, t.statut) * ligneUnitPrix(l), 0);

const STATUTS = {
  demande:  { label: 'Demande',  color: '#3b82f6', bg: '#eff6ff' },
  validee:  { label: 'Validée',  color: '#8b5cf6', bg: '#f5f3ff' },
  expediee: { label: 'Expédiée', color: '#f59e0b', bg: '#fffbeb' },
  recue:    { label: 'Reçue',    color: '#16a34a', bg: '#dcfce7' },
  annulee:  { label: 'Annulée',  color: '#dc2626', bg: '#fee2e2' },
};

const PRIORITES = {
  basse:   { label: 'Basse',   color: '#64748b', bg: '#f1f5f9' },
  normale: { label: 'Normale', color: '#3b82f6', bg: '#eff6ff' },
  haute:   { label: 'Haute',   color: '#f59e0b', bg: '#fffbeb' },
  urgente: { label: 'Urgente', color: '#dc2626', bg: '#fee2e2' },
};

const TYPES = {
  'entrepot-entrepot': 'Entrepôt → Entrepôt',
  'entrepot-boutique': 'Entrepôt → Boutique',
  'boutique-entrepot': 'Boutique → Entrepôt',
  'boutique-boutique': 'Boutique → Boutique',
};

const EMPTY_FORM = {
  typeTransfert: 'entrepot-boutique',
  entrepotSource: '', entrepotDestination: '',
  boutiqueSource: '', boutiqueDestination: '',
  lignes: [{ produit: '', quantiteDemandee: 1 }],
  motif: '', commentaire: '', priorite: 'normale',
};

export default function Transferts() {
  // Réglages entreprise (Configuration → Transferts de stock)
  const { config } = useConfig();
  const transfertDirect = config?.transfert_direct === true || config?.transfert_direct === 'true';
  const typesAutorises = (() => {
    const t = Array.isArray(config?.transfert_types_autorises)
      ? config.transfert_types_autorises.filter(k => TYPES[k]) : [];
    return t.length ? t : Object.keys(TYPES);
  })();
  const typeDefaut = typesAutorises.includes(config?.transfert_type_defaut)
    ? config.transfert_type_defaut : typesAutorises[0];

  const [transferts, setTransferts] = useState([]);
  const [produits,   setProduits]   = useState([]);
  const [entrepots,  setEntrepots]  = useState([]);
  const [boutiques,  setBoutiques]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast,      setToast]      = useState(null);

  const [filters, setFilters] = useState({ statut: '', typeTransfert: '', dateDebut: '', dateFin: '' });

  const [formModal,  setFormModal]  = useState(null); // null | 'create' | 'edit'
  const [formData,   setFormData]   = useState(EMPTY_FORM);
  const [selected,   setSelected]   = useState(null);

  const [validerModal,  setValiderModal]  = useState(null);
  const [expedierModal, setExpedierModal] = useState(null);
  const [recevoirModal, setRecevoirModal] = useState(null);
  const [annulerModal,  setAnnulerModal]  = useState(null);

  const [quantitesExp, setQuantitesExp] = useState({});
  const [quantitesRec, setQuantitesRec] = useState({});
  const [motifAnnul,   setMotifAnnul]   = useState('');
  const [pdfLoading,   setPdfLoading]   = useState({});
  // Picker produit pour les lignes du formulaire de transfert
  const [openLigneIdx, setOpenLigneIdx] = useState(-1);
  const [ligneSearch,  setLigneSearch]  = useState('');

  const nbDemandes  = useMemo(() => transferts.filter(t => t.statut === 'demande').length,  [transferts]);
  const nbValidees  = useMemo(() => transferts.filter(t => t.statut === 'validee').length,  [transferts]);
  const nbExpediees = useMemo(() => transferts.filter(t => t.statut === 'expediee').length, [transferts]);
  const nbRecues    = useMemo(() => transferts.filter(t => t.statut === 'recue').length,    [transferts]);
  // Valeur totale (en achat) des transferts non annulés
  const montantTotal = useMemo(
    () => transferts.filter(t => t.statut !== 'annulee').reduce((s, t) => s + transfertMontant(t), 0),
    [transferts]
  );

  useEffect(() => { loadAll(); }, [filters]); // eslint-disable-line

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.statut)        params.statut        = filters.statut;
      if (filters.typeTransfert) params.typeTransfert = filters.typeTransfert;
      if (filters.dateDebut)     params.dateDebut     = filters.dateDebut;
      if (filters.dateFin)       params.dateFin       = filters.dateFin;

      const [tr, pr, en, bo] = await Promise.all([
        getTransferts(params),
        getProduits(),
        getEntrepots(),
        getBoutiques(),
      ]);

      setTransferts(tr.data.data || []);
      setProduits(pr.data.data   || []);
      setEntrepots(en.data.data  || []);
      // getBoutiques renvoie un tableau brut, pas { data: [] }
      setBoutiques(Array.isArray(bo.data) ? bo.data : (bo.data.data || []));
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => setFilters({ statut: '', typeTransfert: '', dateDebut: '', dateFin: '' });

  // ── Form modal ──
  const openCreate = () => { setFormData({ ...EMPTY_FORM, typeTransfert: typeDefaut }); setSelected(null); setFormModal('create'); };
  const openEdit   = (t) => {
    setSelected(t);
    setFormData({
      typeTransfert:       t.typeTransfert,
      entrepotSource:      t.entrepotSource?._id      || '',
      entrepotDestination: t.entrepotDestination?._id || '',
      boutiqueSource:      t.boutiqueSource?._id      || '',
      boutiqueDestination: t.boutiqueDestination?._id || '',
      lignes: t.lignes.map(l => ({ produit: l.produit._id, quantiteDemandee: l.quantiteDemandee })),
      motif:       t.motif       || '',
      commentaire: t.commentaire || '',
      priorite:    t.priorite,
    });
    setFormModal('edit');
  };
  const closeForm = () => { setFormModal(null); setSelected(null); };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'typeTransfert') {
      setFormData(prev => ({
        ...prev, typeTransfert: value,
        entrepotSource: '', entrepotDestination: '',
        boutiqueSource: '', boutiqueDestination: '',
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleLigneChange = (i, field, value) => {
    const lignes = [...formData.lignes];
    lignes[i] = { ...lignes[i], [field]: field === 'quantiteDemandee' ? (parseInt(value) || 1) : value };
    setFormData(prev => ({ ...prev, lignes }));
  };

  const addLigne    = () => setFormData(prev => ({ ...prev, lignes: [...prev.lignes, { produit: '', quantiteDemandee: 1 }] }));
  const removeLigne = (i) => setFormData(prev => ({ ...prev, lignes: prev.lignes.filter((_, idx) => idx !== i) }));

  const buildPayload = () => {
    const t = formData.typeTransfert;
    const payload = {
      typeTransfert: t,
      lignes:        formData.lignes,
      motif:         formData.motif,
      commentaire:   formData.commentaire,
      priorite:      formData.priorite,
    };
    if (t === 'entrepot-entrepot' || t === 'entrepot-boutique') payload.entrepotSource      = formData.entrepotSource;
    if (t === 'boutique-entrepot' || t === 'boutique-boutique') payload.boutiqueSource      = formData.boutiqueSource;
    if (t === 'entrepot-entrepot' || t === 'boutique-entrepot') payload.entrepotDestination = formData.entrepotDestination;
    if (t === 'entrepot-boutique' || t === 'boutique-boutique') payload.boutiqueDestination = formData.boutiqueDestination;
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = buildPayload();
      if (formModal === 'create') {
        const { data } = await createTransfert(payload);
        showToast('success', data?.message || 'Transfert créé avec succès');
      } else {
        await updateTransfert(selected._id, payload);
        showToast('success', 'Transfert modifié avec succès');
      }
      closeForm();
      loadAll();
    } catch (err) {
      showToast('error', err.response?.data?.message || "Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce transfert ?')) return;
    try {
      await deleteTransfert(id);
      showToast('success', 'Transfert supprimé');
      loadAll();
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  // ── Workflow ──
  const handleValider = async () => {
    setSubmitting(true);
    try {
      await validerTransfert(validerModal._id);
      showToast('success', 'Transfert validé');
      setValiderModal(null);
      loadAll();
    } catch (err) { showToast('error', err.response?.data?.message || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  const openExpedier = (t) => {
    const q = {};
    t.lignes.forEach(l => { q[l.produit._id] = l.quantiteDemandee; });
    setQuantitesExp(q);
    setExpedierModal(t);
  };

  const handleExpedier = async () => {
    setSubmitting(true);
    try {
      await expedierTransfert(expedierModal._id, { quantitesExpediees: quantitesExp });
      showToast('success', 'Transfert expédié');
      setExpedierModal(null);
      loadAll();
    } catch (err) { showToast('error', err.response?.data?.message || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  const openRecevoir = (t) => {
    const q = {};
    t.lignes.forEach(l => { q[l.produit._id] = l.quantiteExpediee; });
    setQuantitesRec(q);
    setRecevoirModal(t);
  };

  const handleRecevoir = async () => {
    setSubmitting(true);
    try {
      await recevoirTransfert(recevoirModal._id, { quantitesRecues: quantitesRec });
      showToast('success', 'Transfert reçu');
      setRecevoirModal(null);
      loadAll();
    } catch (err) { showToast('error', err.response?.data?.message || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  const handleAnnuler = async () => {
    if (!motifAnnul.trim()) { showToast('error', 'Le motif est requis'); return; }
    setSubmitting(true);
    try {
      await annulerTransfert(annulerModal._id, { motifAnnulation: motifAnnul });
      showToast('success', 'Transfert annulé');
      setAnnulerModal(null);
      setMotifAnnul('');
      loadAll();
    } catch (err) { showToast('error', err.response?.data?.message || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  const ouvrirBon = async (id) => {
    setPdfLoading(p => ({ ...p, [id]: true }));
    try {
      const res = await getBonTransfertPDF(id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch { showToast('error', 'Erreur lors de la génération du bon'); }
    finally { setPdfLoading(p => ({ ...p, [id]: false })); }
  };

  // ── Helpers ──
  const srcName     = (t) => t.entrepotSource?.nom      || t.boutiqueSource?.nom      || '—';
  const dstName     = (t) => t.entrepotDestination?.nom || t.boutiqueDestination?.nom || '—';
  const isSrcEntrepot = (t) => !!t.entrepotSource;
  const isDstEntrepot = (t) => !!t.entrepotDestination;

  const showEntrepotSrc = formData.typeTransfert === 'entrepot-entrepot' || formData.typeTransfert === 'entrepot-boutique';
  const showBoutiqueSrc = formData.typeTransfert === 'boutique-entrepot' || formData.typeTransfert === 'boutique-boutique';
  const showEntrepotDst = formData.typeTransfert === 'entrepot-entrepot' || formData.typeTransfert === 'boutique-entrepot';
  const showBoutiqueDst = formData.typeTransfert === 'entrepot-boutique' || formData.typeTransfert === 'boutique-boutique';

  if (loading) return (
    <div className="tr-loading"><Loader className="tr-spinner" size={26}/><span>Chargement…</span></div>
  );

  return (
    <div className="tr-page">

      {/* Toast */}
      {toast && (
        <div className={`tr-toast tr-toast--${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={13}/> : <AlertCircle size={13}/>}
          {toast.text}
        </div>
      )}

      {/* ── Header ── */}
      <div className="tr-header">
        <div className="tr-header-left">
          <div className="tr-header-icon"><Package size={20}/></div>
          <div>
            <h1 className="tr-header-title">Transferts de Stock</h1>
            <p className="tr-header-sub">Mouvements entre entrepôts et boutiques</p>
          </div>
        </div>
        <button className="tr-new-btn" onClick={openCreate}><Plus size={15}/> Nouveau transfert</button>
      </div>

      <div className="tr-content">

        {/* KPI */}
        <div className="tr-kpi-row">
          {[
            { label: 'Total',     value: transferts.length, color: '#6366f1', bg: '#eef2ff', icon: Package   },
            { label: 'Demandes',  value: nbDemandes,        color: '#3b82f6', bg: '#eff6ff', icon: Clock     },
            { label: 'Validées',  value: nbValidees,        color: '#8b5cf6', bg: '#f5f3ff', icon: CheckCircle },
            { label: 'Expédiées', value: nbExpediees,       color: '#f59e0b', bg: '#fffbeb', icon: Send      },
            { label: 'Reçues',    value: nbRecues,          color: '#16a34a', bg: '#dcfce7', icon: TrendingUp },
            { label: 'Valeur',    value: fmt(montantTotal), color: '#0f172a', bg: '#f1f5f9', icon: FileText  },
          ].map((k, i) => {
            const Icon = k.icon;
            return (
              <div key={i} className="tr-kpi" style={{ borderTopColor: k.color }}>
                <div className="tr-kpi-icon" style={{ background: k.bg, color: k.color }}><Icon size={14}/></div>
                <div className="tr-kpi-val">{k.value}</div>
                <div className="tr-kpi-label">{k.label}</div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="tr-filters">
          <Filter size={13} style={{ color: '#94a3b8', flexShrink: 0 }}/>
          <select className="tr-select" value={filters.statut}
            onChange={e => setFilters({ ...filters, statut: e.target.value })}>
            <option value="">Tous les statuts</option>
            <option value="demande">Demande</option>
            <option value="validee">Validée</option>
            <option value="expediee">Expédiée</option>
            <option value="recue">Reçue</option>
            <option value="annulee">Annulée</option>
          </select>
          <select className="tr-select" value={filters.typeTransfert}
            onChange={e => setFilters({ ...filters, typeTransfert: e.target.value })}>
            <option value="">Tous les types</option>
            <option value="entrepot-entrepot">Entrepôt → Entrepôt</option>
            <option value="entrepot-boutique">Entrepôt → Boutique</option>
            <option value="boutique-entrepot">Boutique → Entrepôt</option>
            <option value="boutique-boutique">Boutique → Boutique</option>
          </select>
          <input className="tr-date-input" type="date" value={filters.dateDebut}
            onChange={e => setFilters({ ...filters, dateDebut: e.target.value })} title="Date début"/>
          <input className="tr-date-input" type="date" value={filters.dateFin}
            onChange={e => setFilters({ ...filters, dateFin: e.target.value })} title="Date fin"/>
          <button className="tr-reset-btn" onClick={resetFilters}><RotateCcw size={13}/> Réinitialiser</button>
          <span className="tr-count">{transferts.length} résultat{transferts.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Table */}
        {transferts.length === 0 ? (
          <div className="tr-empty">
            <div className="tr-empty-icon"><CheckCircle size={32}/></div>
            <h3>Aucun transfert</h3>
            <p>Créez votre premier transfert de stock</p>
          </div>
        ) : (
          <div className="tr-card">
            <table className="tr-table">
              <thead>
                <tr>
                  <th>Numéro</th>
                  <th>Route</th>
                  <th>Type</th>
                  <th>Articles</th>
                  <th className="tr-th-right">Montant</th>
                  <th>Priorité</th>
                  <th>Statut</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {transferts.map(t => {
                  const st = STATUTS[t.statut]    || STATUTS.demande;
                  const pr = PRIORITES[t.priorite] || PRIORITES.normale;
                  const montant = transfertMontant(t);
                  return (
                    <tr key={t._id}>
                      <td><span className="tr-num">{t.numeroTransfert}</span></td>
                      <td>
                        <div className="tr-route">
                          <div className="tr-route-loc">
                            {isSrcEntrepot(t) ? <Warehouse size={11}/> : <Store size={11}/>}
                            <span>{srcName(t)}</span>
                          </div>
                          <ArrowRight size={11} className="tr-route-arrow"/>
                          <div className="tr-route-loc">
                            {isDstEntrepot(t) ? <Warehouse size={11}/> : <Store size={11}/>}
                            <span>{dstName(t)}</span>
                          </div>
                        </div>
                      </td>
                      <td><span className="tr-type">{TYPES[t.typeTransfert] || t.typeTransfert}</span></td>
                      <td><span className="tr-articles">{t.lignes.length} art.</span></td>
                      <td className="tr-td-montant">{fmt(montant)}</td>
                      <td><span className="tr-badge" style={{ background: pr.bg, color: pr.color }}>{pr.label}</span></td>
                      <td><span className="tr-badge" style={{ background: st.bg, color: st.color }}>{st.label}</span></td>
                      <td className="tr-date">{fdate(t.dateDemande)}</td>
                      <td>
                        <div className="tr-row-actions">
                          {t.statut === 'demande' && (<>
                            <button className="tr-btn tr-btn--edit"   title="Modifier"  onClick={() => openEdit(t)}><Edit2 size={13}/></button>
                            <button className="tr-btn tr-btn--green"  title="Valider"   onClick={() => setValiderModal(t)}><Check size={13}/></button>
                            <button className="tr-btn tr-btn--danger" title="Supprimer" onClick={() => handleDelete(t._id)}><Trash2 size={13}/></button>
                          </>)}
                          {t.statut === 'validee' && (<>
                            <button className="tr-btn tr-btn--primary" title="Expédier" onClick={() => openExpedier(t)}><Send size={13}/></button>
                            <button className="tr-btn tr-btn--danger"  title="Annuler"  onClick={() => { setAnnulerModal(t); setMotifAnnul(''); }}><X size={13}/></button>
                          </>)}
                          {t.statut === 'expediee' && (<>
                            <button className="tr-btn tr-btn--green"  title="Recevoir" onClick={() => openRecevoir(t)}><Check size={13}/></button>
                            <button className="tr-btn tr-btn--danger" title="Annuler"  onClick={() => { setAnnulerModal(t); setMotifAnnul(''); }}><X size={13}/></button>
                          </>)}
                          {t.statut !== 'demande' && (
                            <button
                              className="tr-btn tr-btn--pdf"
                              title="Bon de transfert PDF"
                              onClick={() => ouvrirBon(t._id)}
                              disabled={!!pdfLoading[t._id]}>
                              {pdfLoading[t._id]
                                ? <Loader size={13} className="tr-spin"/>
                                : <FileText size={13}/>}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ══════════════ Modal Création / Édition ══════════════ */}
      {formModal && (
        <>
          <div className="tr-overlay" onClick={closeForm}/>
          <div className="tr-modal tr-modal--lg">
            <div className="tr-modal-head">
              <div>
                <h2 className="tr-modal-title">
                  {formModal === 'create'
                    ? <><Plus size={15}/> Nouveau transfert</>
                    : <><Edit2 size={15}/> Modifier le transfert</>}
                </h2>
                {formModal === 'edit' && <p className="tr-modal-sub">{selected?.numeroTransfert}</p>}
              </div>
              <button className="tr-modal-close" onClick={closeForm}><X size={16}/></button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="tr-modal-body">

                {/* Type + Priorité */}
                <div className="tr-form-row2">
                  <div className="tr-field">
                    <label>Type de transfert *</label>
                    <select name="typeTransfert" value={formData.typeTransfert} onChange={handleChange} required>
                      {Object.entries(TYPES)
                        .filter(([k]) => typesAutorises.includes(k) || k === formData.typeTransfert)
                        .map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                    </select>
                  </div>
                  <div className="tr-field">
                    <label>Priorité</label>
                    <select name="priorite" value={formData.priorite} onChange={handleChange}>
                      <option value="basse">Basse</option>
                      <option value="normale">Normale</option>
                      <option value="haute">Haute</option>
                      <option value="urgente">Urgente</option>
                    </select>
                  </div>
                </div>

                {formModal === 'create' && transfertDirect && (
                  <div className="tr-direct-note">
                    <AlertCircle size={13}/> Transfert direct activé : le stock sera déplacé immédiatement de la source vers la destination.
                  </div>
                )}

                {/* Source + Destination */}
                <div className="tr-form-row2">
                  {showEntrepotSrc && (
                    <div className="tr-field">
                      <label>Entrepôt source *</label>
                      <select name="entrepotSource" value={formData.entrepotSource} onChange={handleChange} required>
                        <option value="">Sélectionner…</option>
                        {entrepots.map(e => <option key={e._id} value={e._id}>{e.nom}</option>)}
                      </select>
                    </div>
                  )}
                  {showBoutiqueSrc && (
                    <div className="tr-field">
                      <label>Boutique source *</label>
                      <select name="boutiqueSource" value={formData.boutiqueSource} onChange={handleChange} required>
                        <option value="">Sélectionner…</option>
                        {boutiques.map(b => <option key={b._id} value={b._id}>{b.nom}</option>)}
                      </select>
                    </div>
                  )}
                  {showEntrepotDst && (
                    <div className="tr-field">
                      <label>Entrepôt destination *</label>
                      <select name="entrepotDestination" value={formData.entrepotDestination} onChange={handleChange} required>
                        <option value="">Sélectionner…</option>
                        {entrepots.map(e => <option key={e._id} value={e._id}>{e.nom}</option>)}
                      </select>
                    </div>
                  )}
                  {showBoutiqueDst && (
                    <div className="tr-field">
                      <label>Boutique destination *</label>
                      <select name="boutiqueDestination" value={formData.boutiqueDestination} onChange={handleChange} required>
                        <option value="">Sélectionner…</option>
                        {boutiques.map(b => <option key={b._id} value={b._id}>{b.nom}</option>)}
                      </select>
                    </div>
                  )}
                </div>

                {/* Motif + Commentaire */}
                <div className="tr-form-row2">
                  <div className="tr-field">
                    <label>Motif <span className="tr-opt">(optionnel)</span></label>
                    <input type="text" name="motif" value={formData.motif} onChange={handleChange} placeholder="Motif du transfert…"/>
                  </div>
                  <div className="tr-field">
                    <label>Commentaire <span className="tr-opt">(optionnel)</span></label>
                    <input type="text" name="commentaire" value={formData.commentaire} onChange={handleChange} placeholder="Note…"/>
                  </div>
                </div>

                {/* Lignes produits */}
                <div className="tr-lignes-head">
                  <span className="tr-lignes-title">Produits à transférer</span>
                  <button type="button" className="tr-add-ligne-btn" onClick={addLigne}><Plus size={13}/> Ajouter</button>
                </div>
                {formData.lignes.map((l, i) => {
                  const sel = produits.find(p => p._id === l.produit);
                  const isOpen = openLigneIdx === i;
                  const filtered = !ligneSearch.trim() ? produits : produits.filter(p => {
                    const q = ligneSearch.toLowerCase().trim();
                    return (p.nom || '').toLowerCase().includes(q)
                        || (p.codeProduit || '').toLowerCase().includes(q)
                        || (p.codeBarres || '').toLowerCase().includes(q);
                  });
                  return (
                    <div key={i} className="tr-ligne">
                      <div className="tr-pp">
                        <button type="button"
                          className={`tr-pp-trigger${sel ? ' tr-pp-trigger--filled' : ''}${isOpen ? ' tr-pp-trigger--open' : ''}`}
                          onClick={() => { setOpenLigneIdx(isOpen ? -1 : i); setLigneSearch(''); }}>
                          <Search size={13} className="tr-pp-trigger-ico"/>
                          {sel ? (
                            <span className="tr-pp-trigger-text">
                              <span className="tr-pp-trigger-nom">{sel.nom}</span>
                              <span className="tr-pp-trigger-sep">·</span>
                              <span className="tr-pp-trigger-code">{sel.codeProduit}</span>
                            </span>
                          ) : (
                            <span className="tr-pp-trigger-placeholder">Choisir un produit…</span>
                          )}
                        </button>
                        {isOpen && (
                          <>
                            <div className="tr-pp-backdrop" onClick={() => setOpenLigneIdx(-1)}/>
                            <div className="tr-pp-dropdown">
                              <div className="tr-pp-search-wrap">
                                <Search size={15} className="tr-pp-search-ico"/>
                                <input type="text"
                                  className="tr-pp-search-input"
                                  placeholder="Rechercher un produit…"
                                  value={ligneSearch}
                                  onChange={e => setLigneSearch(e.target.value)}
                                  autoFocus/>
                                {ligneSearch && (
                                  <button type="button" className="tr-pp-search-clear" onClick={() => setLigneSearch('')}>
                                    <X size={13}/>
                                  </button>
                                )}
                              </div>
                              <div className="tr-pp-list">
                                {filtered.length === 0 ? (
                                  <div className="tr-pp-empty"><Package size={20}/><span>Aucun produit trouvé</span></div>
                                ) : (
                                  <>
                                    <div className="tr-pp-list-header">{filtered.length} produit{filtered.length > 1 ? 's' : ''}</div>
                                    {filtered.slice(0, 50).map(p => {
                                      const isActive = l.produit === p._id;
                                      const stock = p.quantiteStock || 0;
                                      return (
                                        <div key={p._id}
                                          className={`tr-pp-item${isActive ? ' tr-pp-item--active' : ''}`}
                                          onClick={() => {
                                            handleLigneChange(i, 'produit', p._id);
                                            setOpenLigneIdx(-1);
                                            setLigneSearch('');
                                          }}>
                                          <div className="tr-pp-item-main">
                                            <div className="tr-pp-item-nom">{p.nom}</div>
                                            <div className="tr-pp-item-code">{p.codeProduit}</div>
                                          </div>
                                          <div className="tr-pp-item-stock">
                                            <span className="tr-pp-item-stock-val">{stock}</span>
                                            <span className="tr-pp-item-stock-lbl">en stock</span>
                                          </div>
                                          {isActive && <Check size={14} className="tr-pp-item-check"/>}
                                        </div>
                                      );
                                    })}
                                    {filtered.length > 50 && (
                                      <div className="tr-pp-more">+{filtered.length - 50} autres — affinez la recherche</div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                        {/* Champ caché pour validation HTML */}
                        <input type="hidden" value={l.produit} required/>
                      </div>
                      <input
                        type="number" min="1"
                        value={l.quantiteDemandee}
                        onChange={e => handleLigneChange(i, 'quantiteDemandee', e.target.value)}
                        required className="tr-ligne-qty"/>
                      <button
                        type="button" className="tr-ligne-del"
                        onClick={() => removeLigne(i)}
                        disabled={formData.lignes.length === 1}>
                        <X size={14}/>
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="tr-modal-footer">
                <button type="button" className="tr-btn-cancel" onClick={closeForm}>Annuler</button>
                <button type="submit" className="tr-btn-confirm" disabled={submitting}>
                  {submitting
                    ? <><Loader size={13} className="tr-spin"/> Enregistrement…</>
                    : <><Check size={13}/> {formModal === 'create' ? (transfertDirect ? 'Transférer maintenant' : 'Créer le transfert') : 'Enregistrer'}</>}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* ══════════════ Modal Validation ══════════════ */}
      {validerModal && (
        <>
          <div className="tr-overlay" onClick={() => setValiderModal(null)}/>
          <div className="tr-modal tr-modal--sm">
            <div className="tr-modal-head">
              <h2 className="tr-modal-title"><Check size={15}/> Valider le transfert</h2>
              <button className="tr-modal-close" onClick={() => setValiderModal(null)}><X size={16}/></button>
            </div>
            <div className="tr-modal-body">
              <p className="tr-confirm-text">Valider le transfert <strong>{validerModal.numeroTransfert}</strong> ?</p>
              <p className="tr-confirm-sub">La disponibilité des produits à la source sera vérifiée.</p>
            </div>
            <div className="tr-modal-footer">
              <button className="tr-btn-cancel" onClick={() => setValiderModal(null)}>Annuler</button>
              <button className="tr-btn-confirm tr-btn-confirm--green" onClick={handleValider} disabled={submitting}>
                {submitting ? <><Loader size={13} className="tr-spin"/> Validation…</> : <><Check size={13}/> Valider</>}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ══════════════ Modal Expédition ══════════════ */}
      {expedierModal && (
        <>
          <div className="tr-overlay" onClick={() => setExpedierModal(null)}/>
          <div className="tr-modal">
            <div className="tr-modal-head">
              <div>
                <h2 className="tr-modal-title"><Send size={15}/> Expédier le transfert</h2>
                <p className="tr-modal-sub">{expedierModal.numeroTransfert}</p>
              </div>
              <button className="tr-modal-close" onClick={() => setExpedierModal(null)}><X size={16}/></button>
            </div>
            <div className="tr-modal-body">
              <div className="tr-qty-section">
                <div className="tr-qty-head">
                  <span>Produit</span><span>Demandé</span><span>Qté expédiée</span>
                </div>
                {expedierModal.lignes.map(l => (
                  <div key={l.produit._id} className="tr-qty-row">
                    <span className="tr-qty-name">{l.produit.nom}</span>
                    <span className="tr-qty-ref">{l.quantiteDemandee}</span>
                    <input
                      type="number" min="0" max={l.quantiteDemandee} className="tr-qty-input"
                      value={quantitesExp[l.produit._id] ?? l.quantiteDemandee}
                      onChange={e => setQuantitesExp(p => ({ ...p, [l.produit._id]: parseInt(e.target.value) || 0 }))}/>
                  </div>
                ))}
              </div>
            </div>
            <div className="tr-modal-footer">
              <button className="tr-btn-cancel" onClick={() => setExpedierModal(null)}>Annuler</button>
              <button className="tr-btn-confirm" onClick={handleExpedier} disabled={submitting}>
                {submitting ? <><Loader size={13} className="tr-spin"/> Expédition…</> : <><Send size={13}/> Expédier</>}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ══════════════ Modal Réception ══════════════ */}
      {recevoirModal && (
        <>
          <div className="tr-overlay" onClick={() => setRecevoirModal(null)}/>
          <div className="tr-modal">
            <div className="tr-modal-head">
              <div>
                <h2 className="tr-modal-title"><Check size={15}/> Réceptionner le transfert</h2>
                <p className="tr-modal-sub">{recevoirModal.numeroTransfert}</p>
              </div>
              <button className="tr-modal-close" onClick={() => setRecevoirModal(null)}><X size={16}/></button>
            </div>
            <div className="tr-modal-body">
              <div className="tr-qty-section">
                <div className="tr-qty-head">
                  <span>Produit</span><span>Expédié</span><span>Qté reçue</span>
                </div>
                {recevoirModal.lignes.map(l => (
                  <div key={l.produit._id} className="tr-qty-row">
                    <span className="tr-qty-name">{l.produit.nom}</span>
                    <span className="tr-qty-ref">{l.quantiteExpediee}</span>
                    <input
                      type="number" min="0" max={l.quantiteExpediee} className="tr-qty-input"
                      value={quantitesRec[l.produit._id] ?? l.quantiteExpediee}
                      onChange={e => setQuantitesRec(p => ({ ...p, [l.produit._id]: parseInt(e.target.value) || 0 }))}/>
                  </div>
                ))}
              </div>
            </div>
            <div className="tr-modal-footer">
              <button className="tr-btn-cancel" onClick={() => setRecevoirModal(null)}>Annuler</button>
              <button className="tr-btn-confirm tr-btn-confirm--green" onClick={handleRecevoir} disabled={submitting}>
                {submitting ? <><Loader size={13} className="tr-spin"/> Réception…</> : <><Check size={13}/> Réceptionner</>}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ══════════════ Modal Annulation ══════════════ */}
      {annulerModal && (
        <>
          <div className="tr-overlay" onClick={() => setAnnulerModal(null)}/>
          <div className="tr-modal tr-modal--sm">
            <div className="tr-modal-head">
              <div>
                <h2 className="tr-modal-title" style={{ color: '#dc2626' }}><X size={15}/> Annuler le transfert</h2>
                <p className="tr-modal-sub">{annulerModal.numeroTransfert}</p>
              </div>
              <button className="tr-modal-close" onClick={() => setAnnulerModal(null)}><X size={16}/></button>
            </div>
            <div className="tr-modal-body">
              <div className="tr-field">
                <label>Motif d'annulation *</label>
                <textarea rows="3" value={motifAnnul}
                  onChange={e => setMotifAnnul(e.target.value)}
                  placeholder="Expliquez le motif d'annulation…"/>
              </div>
            </div>
            <div className="tr-modal-footer">
              <button className="tr-btn-cancel" onClick={() => setAnnulerModal(null)}>Retour</button>
              <button
                className="tr-btn-confirm tr-btn-confirm--danger"
                onClick={handleAnnuler}
                disabled={submitting || !motifAnnul.trim()}>
                {submitting
                  ? <><Loader size={13} className="tr-spin"/> Annulation…</>
                  : <><X size={13}/> Confirmer l'annulation</>}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
